// A boat's own photo for its home header (TOM-683, cloud ADR 0397; contract
// §5.22): where home fetches it, which files Settings tries, and the words
// Settings says when an upload is refused.
//
// The server reads the type from the bytes and is the judge (JPEG, PNG or
// WebP, at most 5 MB and 8000 px a side). Settings draws every photo again as
// a JPEG before it uploads (prepareChatPhoto with redraw): that strips its
// location whatever its type and shrinks a large one, so the server's caps
// are rarely met.
//
// Pure, so cloud/web's Node vitest covers it.

import type { BoatPhoto } from './useBoats';

/** The types the picker offers: HEIC is redrawn as a JPEG where the browser reads it. */
export const BOAT_PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif';

/**
 * Whether Settings tries a picked file as a photo. A file the browser could
 * not type (a photo saved with no extension comes as "" or an octet stream)
 * is tried too: it is drawn again before it uploads, and the server reads the
 * type from the bytes. A file declared as anything else is refused here.
 */
export function boatPhotoMayBe(file: { type: string; name: string }): boolean {
  const type = file.type.toLowerCase();
  if (type === '' || type === 'application/octet-stream') return true;
  return type.startsWith('image/');
}

/** The photo's address. A new photo has a new version, so a new address. */
export function boatPhotoUrl(boatId: string, photo: BoatPhoto | null | undefined): string | null {
  if (photo === null || photo === undefined) return null;
  return `/v1/boats/${encodeURIComponent(boatId)}/photo?v=${encodeURIComponent(photo.version)}`;
}

/** The sentence for a refused upload or removal. */
export function boatPhotoErrorMessage(status: number, body: unknown): string {
  const code =
    body !== null &&
    typeof body === 'object' &&
    typeof (body as { error?: unknown }).error === 'string'
      ? (body as { error: string }).error
      : null;
  if (status === 413) return 'That photo is too large. Choose one under 5 MB.';
  if (status === 415) return 'Choose a JPEG, PNG or WebP photo.';
  if (status === 400 && code === 'invalid_photo')
    return 'That photo couldn’t be read. Try another.';
  if (status === 401) return 'Sign in again, then try again.';
  if (status === 403) return 'Reload the page, then try again.';
  if (status === 404) return 'This boat is no longer on your account.';
  if (status === 429) return 'Too many changes at once. Try again in a minute.';
  return 'The photo didn’t save. Try again.';
}
